import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Chip,
    Grid,
    Divider,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import {
    ArrowBack,
    CheckCircle,
    Lock,
    PlayArrow,
    MenuBook
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";

export default function LearningPathDetailPage() {
    const navigate = useNavigate();
    const { pathId } = useParams();
    const { userProfile } = useAuth();
    const [path, setPath] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [openLevelDialog, setOpenLevelDialog] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const pathData = await learningService.getLearningPathById(pathId);
                setPath(pathData);

                if (userProfile?.uid) {
                    const progressData = await learningService.getStudentProgress(
                        userProfile.uid,
                        pathId
                    );
                    setProgress(progressData);
                }
            } catch (error) {
                console.error("Error fetching path details:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pathId, userProfile]);

    const getLevelStatus = (levelId) => {
        return progress?.levelStatus?.[levelId] || {
            status: "locked",
            attempts: 0,
            cleared: false
        };
    };

    const getDifficultyColor = (difficulty) => {
        const colors = {
            Beginner: "success",
            Intermediate: "warning",
            Advanced: "error"
        };
        return colors[difficulty] || "default";
    };

    const handleLevelClick = (level) => {
        setSelectedLevel(level);
        setOpenLevelDialog(true);
    };

    const handleStartTest = async () => {
        setOpenLevelDialog(false);
        navigate(`/student/learning/test/${pathId}/${selectedLevel.id}`);
    };

    if (loading) {
        return (
            <AdminLayout title="Learning Path Details">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    if (!path) {
        return (
            <AdminLayout title="Learning Path Details">
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">Learning path not found</Alert>
                    <Button
                        variant="contained"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate("/student/learning/paths")}
                        sx={{ mt: 2 }}
                    >
                        Back to Paths
                    </Button>
                </Box>
            </AdminLayout>
        );
    }

    const levels = path.levels || [];
    const clearedLevels = levels.filter(
        (l) => getLevelStatus(l.id).cleared
    ).length;
    const progressPercentage = levels.length > 0 ? (clearedLevels / levels.length) * 100 : 0;

    return (
        <AdminLayout title="Learning Path Details">
            <Container maxWidth="lg" sx={{ py: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate("/student/learning/paths")}
                        sx={{ mb: 2 }}
                    >
                        Back to Paths
                    </Button>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                {path.name}
                            </Typography>
                            <Chip
                                label={path.difficulty}
                                color={getDifficultyColor(path.difficulty)}
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "success.main" }}>
                                {path.creditsOnCompletion} Credits
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                on completion
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Progress Card */}
                <Card sx={{ mb: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                        Overall Progress
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                                        {Math.round(progressPercentage)}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progressPercentage}
                                        sx={{
                                            mt: 1,
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: "rgba(255,255,255,0.2)"
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ textAlign: "right" }}>
                                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                        Levels Cleared
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                                        {clearedLevels}/{levels.length}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Learning Material */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <MenuBook /> Learning Material
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}>
                            {path.description}
                        </Typography>
                    </CardContent>
                </Card>

                {/* Levels */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                            Levels ({levels.length})
                        </Typography>

                        {levels.length === 0 ? (
                            <Alert severity="info">No levels available</Alert>
                        ) : (
                            <Stepper orientation="vertical">
                                {levels.map((level, index) => {
                                    const levelStatus = getLevelStatus(level.id);
                                    const isLocked = levelStatus.status === "locked" && index > 0 && !getLevelStatus(levels[index - 1]?.id).cleared;

                                    return (
                                        <Step key={level.id} completed={levelStatus.cleared} active={!levelStatus.cleared && !isLocked}>
                                            <StepLabel
                                                icon={
                                                    isLocked ? (
                                                        <Lock sx={{ color: "text.secondary" }} />
                                                    ) : levelStatus.cleared ? (
                                                        <CheckCircle sx={{ color: "success.main" }} />
                                                    ) : undefined
                                                }
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        width: "100%",
                                                        pr: 2
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                            {level.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {level.description}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                        <Chip
                                                            label={`${level.questions?.length || 0} Q`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        <Chip
                                                            label={`${level.qualifyingScore}%`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                </Box>
                                            </StepLabel>
                                            <Box sx={{ ml: 4, pb: 2 }}>
                                                {levelStatus.cleared && (
                                                    <Box sx={{ mb: 1 }}>
                                                        <Chip
                                                            icon={<CheckCircle />}
                                                            label={`Cleared - Score: ${levelStatus.highestScore}%`}
                                                            color="success"
                                                            size="small"
                                                        />
                                                    </Box>
                                                )}
                                                {levelStatus.attempts > 0 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Attempts: {levelStatus.attempts}
                                                    </Typography>
                                                )}
                                                {!isLocked && (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        startIcon={<PlayArrow />}
                                                        onClick={() => handleLevelClick(level)}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        {levelStatus.cleared ? "Review" : "Attempt"}
                                                    </Button>
                                                )}
                                                {isLocked && (
                                                    <Alert severity="info" sx={{ mt: 1 }}>
                                                        Complete previous level to unlock
                                                    </Alert>
                                                )}
                                            </Box>
                                        </Step>
                                    );
                                })}
                            </Stepper>
                        )}
                    </CardContent>
                </Card>
            </Container>

            {/* Level Detail Dialog */}
            <Dialog
                open={openLevelDialog}
                onClose={() => setOpenLevelDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{selectedLevel?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Description
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {selectedLevel?.description}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                    Questions
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {selectedLevel?.questions?.length || 0}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                    Passing Score
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {selectedLevel?.qualifyingScore}%
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                    Duration
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {selectedLevel?.duration}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                    Attempts
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {getLevelStatus(selectedLevel?.id)?.attempts || 0}
                                </Typography>
                            </Grid>
                        </Grid>

                        {getLevelStatus(selectedLevel?.id)?.cleared && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                                ✅ Level Cleared - Score: {getLevelStatus(selectedLevel?.id)?.highestScore}%
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenLevelDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleStartTest}
                        startIcon={<PlayArrow />}
                    >
                        {getLevelStatus(selectedLevel?.id)?.cleared ? "Review" : "Start Test"}
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminLayout>
    );
}
